import { useEffect, useState } from "react";
import QRCode from "qrcode";

/**
 * THE BADGE'S QR — the one the entrance scanner expects, not a decoration.
 *
 * ⚠️ IT ENCODES `UserEvents.code` EXACTLY, AND THAT IS CHECKED IN THE PRODUCT rather
 * than assumed: the printed badge goes through
 * `api/src/routes/badgeTemplates/render/index.js`, whose subject carries
 * `qr: r.code || r.voucherCode || String(r.id)`. V1 draws the same thing on screen
 * (`screens/Home.jsx`: `QRCode.toDataURL(UserEvent.code)`). A QR encoding anything else
 * — the internal id, a URL — would scan without complaint and let somebody through on
 * nothing: the one defect of this screen that cannot be seen by looking at it.
 *
 * ⚠️ THE HOUSE LIBRARY (`qrcode`): it is already used by `fo-meeting`, `bo`,
 * `fo-exhibitor` AND by the API that prints the badge. Two encoders for one code means
 * two sets of defaults to compare the day a badge will not scan.
 *
 * ⚠️ BLACK INK ON WHITE, NOT THE BRAND COLOUR. The prototype tints it
 * `--gk-brand-700`; its own review had already noted that a pale blue QR "does not read
 * as a QR". Here the stakes are beyond style: a reader wants sharp contrast, and the
 * brand colour is a SETTING — a client in yellow would get a badge their own entrance
 * scanners cannot read, with nothing warning them.
 */

/** The quiet zone, in modules. The format asks for 4; the badge's white box
 *  (`.badge__q`, a padding) provides part of it and these two make up the rest. Below
 *  that, a reader can mistake the edge of the code for page content. */
const QUIET_ZONE = 2;

/** Rendered at twice the displayed size: on a dense screen a pixelated QR scans worse,
 *  and that is exactly the device it will be presented on. */
const DENSITY = 2;

export function QrCode({
  value,
  size = 132,
  label,
  className = "badge__q",
}: {
  value: string;
  size?: number;
  /** What a screen reader announces: whose badge this is. */
  label: string;
  /** The white box around the code. The room screen (`LiveScene`) draws its own:
   *  it encodes the live page's address there, not a badge code. */
  className?: string;
}) {
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(value, {
      width: size * DENSITY,
      margin: QUIET_ZONE,
      color: { dark: "#000000ff", light: "#ffffffff" },
    })
      .then((url) => {
        if (alive) setImage(url);
      })
      /**
       * ⚠️ NOTHING RATHER THAN A BROKEN IMAGE. Encoding fails on an empty or oversized
       * string; the screen then shows the identifier in plain text, which can still be
       * read out at the desk — precisely the job its own setting gives it.
       */
      .catch(() => {
        if (alive) setImage(null);
      });
    return () => {
      alive = false;
    };
  }, [value, size]);

  if (!image) return null;

  return (
    <span className={className}>
      <img src={image} width={size} height={size} alt={label} />
    </span>
  );
}
